// Checks a pasted range against the Apartments sheet before anything is applied (BR-040, BR-047).
// Nothing here is silent: an older workbook layout (17 columns with "תמורה/ רוכשים" and
// "תעודת זהות", or the previous 16 with "תעודת זהות") needs the user's confirmation before its
// extra columns are dropped, and a paste that would put values into restricted, locked or
// unauthorized columns is rejected as a whole, naming the columns. Dropped values are not kept.
import {
  LEGACY_LAYOUTS,
  type LegacyLayout,
  dropLegacyColumns,
  headerRowLayout,
  isPersonalField,
  mayAccessField,
} from '@sivan/shared';
import { type SheetRow, kindOf, lockedReason, rowState } from './apartment-draft';
import { type PasteColumn, checkTargets as checkSheetTargets } from './sheet-paste';

export interface PastePermissions {
  readonly create: boolean;
  readonly edit: boolean;
  readonly allocate: boolean;
  /** Changing the contact columns (projects.contact.view + .edit, DR-185). */
  readonly contact?: boolean;
  /** Changing the note column (projects.note.view + .edit, DR-187). */
  readonly note?: boolean;
}

export type PasteCheck =
  /** Ready to apply. `headerDropped` when the first pasted row was the header row. */
  | { readonly kind: 'ready'; readonly table: string[][]; readonly headerDropped: boolean }
  /**
   * An older layout: the user must confirm dropping its extra columns. `alsoFits` when the range
   * would also fit the sheet's current columns as it is (additional pairs are shown), so the user
   * chooses; it is never guessed.
   */
  | {
      readonly kind: 'legacy';
      readonly layout: LegacyLayout;
      readonly table: string[][];
      readonly headerDropped: boolean;
      readonly alsoFits: boolean;
    }
  | { readonly kind: 'rejected'; readonly message: string };

/** Step 1: recognizes a header row and an older layout. */
export function checkLayout(
  table: readonly string[][],
  anchorColumn: number,
  columnCount: number,
): PasteCheck {
  const [first] = table;
  const header = first === undefined ? null : headerRowLayout(first);
  const body = header === null ? table.map((r) => [...r]) : table.slice(1).map((r) => [...r]);
  const headerDropped = header !== null;
  const width = Math.max(0, ...body.map((r) => r.length));
  // A recognized header decides the layout; only a range without one is judged by its width.
  const layout: LegacyLayout | null =
    header !== null
      ? header === 'sheet'
        ? null
        : header
      : anchorColumn === 0 && width === LEGACY_LAYOUTS.source.width
        ? 'source'
        : anchorColumn === 0 && width === LEGACY_LAYOUTS.previous.width
          ? 'previous'
          : null;
  if (layout === null) return { kind: 'ready', table: body, headerDropped };
  if (anchorColumn !== 0) {
    return {
      kind: 'rejected',
      message: `זוהה מבנה גיליון ישן (${String(LEGACY_LAYOUTS[layout].width)} עמודות). יש להדביק אותו החל מעמודת "בנין". דבר לא הודבק.`,
    };
  }
  return {
    kind: 'legacy',
    layout,
    table: body,
    headerDropped,
    alsoFits: header === null && width <= columnCount,
  };
}

/** After the user confirmed: the older layout's extra columns are removed; their values are not kept. */
export const confirmLegacy = (table: readonly string[][], layout: LegacyLayout): string[][] =>
  dropLegacyColumns(table, layout);

/** Step 2: the table must fit the sheet's columns, and only touch what may be changed. */
export function checkTargets(
  table: readonly string[][],
  displayed: readonly SheetRow[],
  anchorIndex: number,
  anchorColumn: number,
  columns: readonly PasteColumn[],
  permissions: PastePermissions,
): string | null {
  return checkSheetTargets(table, displayed, anchorIndex, anchorColumn, {
    columns,
    isNew: (row) => row === undefined || rowState(row) === 'placeholder',
    locked: (row, key) => lockedReason(row, key),
    allowed: (row, key) =>
      isPersonalField(key)
        ? mayAccessField(key, permissions)
        : kindOf(key) !== undefined
          ? permissions.allocate
          : row === null
            ? permissions.create
            : permissions.edit,
  });
}
